import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { fmtDate, toInputDate } from '../utils';
import { Badge, Modal, Pagination, ConfirmDialog } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const REVENUE_OPTIONS = ['$25M-$100M','$100M to $500M','$500M to $1Bn','$1Bn+'];
const INDUSTRIES = ['Technology','Defense','Healthcare','Finance','Manufacturing','Retail','Education','Consulting','Media','Other'];
const CUSTOMER_TYPES = ['Federal','State','Local','Commercial','Non-Profit'];

export function AccountForm({ account, onSave, onClose }) {
  const [form, setForm] = useState({ name:'',office_division:'',customer_type:'Federal',industry:'',website:'',phone:'',address:'',city:'',state:'',annual_revenue:'',status:'active',notes:'', ...account });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const isFed = form.customer_type === 'Federal';
  const isCom = form.customer_type === 'Commercial';
  const needsLoc = ['State','Local','Commercial','Non-Profit'].includes(form.customer_type);

  const handleSave = async () => {
    if (!form.name.trim()) return setError('Account name required');
    setSaving(true);
    try {
      const r = account?.id ? await api.put(`/accounts/${account.id}`, form) : await api.post('/accounts', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={account?.id ? 'Edit Account' : 'New Account'} onClose={onClose} size="modal-lg">
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Account Name *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Customer Type</label>
            <select className="form-select" value={form.customer_type} onChange={set('customer_type')}>{CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}</select>
          </div>
          <div className="form-group"><label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={set('status')}>
              <option value="active">Active</option><option value="prospect">Prospect</option><option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">{isFed ? 'Office / Division' : 'Division'}</label>
          <input className="form-input" value={form.office_division} onChange={set('office_division')} />
        </div>
        {isCom && <div className="form-grid">
          <div className="form-group"><label className="form-label">Industry</label>
            <select className="form-select" value={form.industry} onChange={set('industry')}>
              <option value="">Select...</option>{INDUSTRIES.map(i => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Annual Revenue</label>
            <select className="form-select" value={form.annual_revenue} onChange={set('annual_revenue')}>
              <option value="">Select...</option>{REVENUE_OPTIONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>}
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website} onChange={set('website')} placeholder="https://..." /></div>
          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={set('phone')} /></div>
        </div>
        {needsLoc && <div className="form-grid">
          <div className="form-group"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={set('city')} /></div>
          <div className="form-group"><label className="form-label">State</label><input className="form-input" value={form.state} onChange={set('state')} /></div>
        </div>}
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={set('notes')} rows={3} /></div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Account'}</button>
      </div>
    </Modal>
  );
}

export default function Accounts() {
  const [accounts, setAccounts] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1);
  const [search, setSearch] = useState(''); const [customerType, setCustomerType] = useState('');
  const [showForm, setShowForm] = useState(false); const [editAcc, setEditAcc] = useState(null); const [archiveId, setArchiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/accounts', { params: { search, customer_type: customerType, page, limit: 25 } }); setAccounts(r.data.data); setTotal(r.data.total); }
    finally { setLoading(false); }
  }, [search, customerType, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="page-header"><span className="page-title">Accounts</span>
        {can('edit') && <button className="btn btn-primary" onClick={() => { setEditAcc(null); setShowForm(true); }}>+ New Account</button>}
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body" style={{ paddingBottom: 12 }}>
            <div className="toolbar">
              <input className="search-input" placeholder="Search accounts..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              <select className="filter-select" value={customerType} onChange={e => { setCustomerType(e.target.value); setPage(1); }}>
                <option value="">All Types</option>{CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <span className="text-muted text-sm" style={{ marginLeft: 'auto' }}>{total} accounts</span>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Account Name</th><th>Type</th><th>Division / Industry</th><th>Location</th><th>Contacts</th><th>Opps</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={8} style={{ textAlign:'center',padding:40,color:'var(--gray-400)' }}>Loading...</td></tr>}
                {!loading && accounts.length === 0 && <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon">🏢</div><div className="empty-state-text">No accounts found</div></div></td></tr>}
                {accounts.map(a => (
                  <tr key={a.id} onClick={() => navigate(`/accounts/${a.id}`)}>
                    <td><span className="td-link">{a.name}</span></td>
                    <td><Badge status={a.customer_type} /></td>
                    <td>{a.office_division || a.industry || '—'}</td>
                    <td>{[a.city,a.state].filter(Boolean).join(', ') || '—'}</td>
                    <td>{a.contact_count||0}</td><td>{a.opportunity_count||0}</td>
                    <td><Badge status={a.status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      {can('edit') && <div style={{ display:'flex',gap:4 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditAcc(a); setShowForm(true); }}>✏️</button>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Archive" onClick={() => setArchiveId(a.id)}>📦</button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} limit={25} total={total} onPage={setPage} />
        </div>
      </div>
      {showForm && <AccountForm account={editAcc} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
      {archiveId && <ConfirmDialog message="Archive this account? Data is preserved but it will be hidden from lists." confirmLabel="Archive" onConfirm={async () => { await api.delete(`/accounts/${archiveId}`); setArchiveId(null); load(); }} onCancel={() => setArchiveId(null)} />}
    </>
  );
}
